// =====================================================================================
// usePhaserGame Hook - Manages Phaser game instance lifecycle
// =====================================================================================
// Extracted from useRoomConnection to isolate game engine management
// Provides centralized control over Phaser game creation, destruction, and state

import { useCallback, useEffect, useRef, useState } from 'react';
import { useGameInstance } from '../contexts/GameInstanceContext';
import type { Game } from 'phaser';
import { restorePhaserKeyboard, type KeyboardGame } from '../utils/phaser-keyboard.js';

// =====================================================================================
// TYPE DEFINITIONS
// =====================================================================================

interface UsePhaserGameReturn {
  /** Whether the Phaser game is ready for operations */
  isGameReady: boolean;
  /** Create the Phaser game instance if it doesn't exist */
  createGame: () => Promise<void>;
  /** Destroy the Phaser game instance */
  destroyGame: () => void;
  /** Get the game scene safely */
  getGameScene: () => any;
  /** Focus the game canvas for keyboard input */
  focusCanvas: () => void;
  /** Set canvas visibility */
  setCanvasVisible: (visible: boolean) => void;
}

// =====================================================================================
// MAIN HOOK
// =====================================================================================

/**
 * usePhaserGame Hook - Manages Phaser game instance lifecycle
 * 
 * This hook provides a clean interface for managing the Phaser game instance,
 * extracted from useRoomConnection for better separation of concerns.
 * 
 * Features:
 * - Lazy game creation (only when needed)
 * - Proper cleanup on unmount
 * - Canvas visibility management
 * - Keyboard focus handling
 * - Integration with GameInstanceContext
 * 
 * @param userId - Current user ID (required for game initialization)
 * @returns Game management interface
 * 
 * @example
 * ```tsx
 * const { createGame, isGameReady, getGameScene } = usePhaserGame(user.id);
 * 
 * useEffect(() => {
 *   if (currentRoom) {
 *     createGame();
 *   }
 * }, [currentRoom]);
 * ```
 */
export function usePhaserGame(userId?: string): UsePhaserGameReturn {
  const { gameInstance, setGameInstance, isGameReady, setIsGameReady } = useGameInstance();
  const [localGameReady, setLocalGameReady] = useState(false);
  const creatingRef = useRef(false);
  // Pending canvas-setup / ready timers, cleared on destroy and unmount so they
  // never fire into an unmounted hook (or a torn-down test environment)
  const pendingTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearPendingTimers = useCallback(() => {
    pendingTimersRef.current.forEach(clearTimeout);
    pendingTimersRef.current = [];
  }, []);

  // =====================================================================================
  // GAME CREATION
  // =====================================================================================

  const createGame = useCallback(async (): Promise<void> => {
    // Prevent multiple simultaneous creation attempts
    if (creatingRef.current || gameInstance) {
      return;
    }

    if (!userId) {
      console.warn('🎮 usePhaserGame: Cannot create game without user ID');
      return;
    }

    creatingRef.current = true;
    console.info('🎮 usePhaserGame: Creating Phaser game instance');

    try {
      // Dynamically import Phaser and game scene
      const [{ GameScene }, PhaserModule] = await Promise.all([
        import('@game/scenes/GameScene.js'),
        import('phaser')
      ]);

      const Phaser = PhaserModule;

      const config: Phaser.Types.Core.GameConfig = {
        type: Phaser.AUTO,
        width: window.innerWidth,
        height: window.innerHeight,
        backgroundColor: '#000000',
        scale: {
          mode: Phaser.Scale.RESIZE,
          autoCenter: Phaser.Scale.CENTER_BOTH
        },
        render: {
          smoothPixelArt: true
        },
        physics: {
          default: 'arcade',
          arcade: {
            gravity: { x: 0, y: 0 },
            debug: true // Enable debug visualization for collision bodies
          }
        },
        dom: {
          enabled: true
        },
        // Keyboard capture is decided per key in MovementKeys (arrows captured, WASD not),
        // not here. Phaser's `input.keyboard.capture` takes KeyCodes names ('UP', 'W'),
        // so the previous DOM-code list ('ArrowUp', 'KeyW') never matched anything.
        scene: [GameScene]
      };

      const game = new Phaser.Game(config) as unknown as Game;
      
      // Store in context
      setGameInstance(game as any);
      
      // Also store in window for backward compatibility (to be removed later)
      (window as any).phaserGame = game;

      // Setup canvas styling and focus handling
      pendingTimersRef.current.push(setTimeout(() => {
        setupCanvas(game);
        
        // Mark as ready after canvas setup
        pendingTimersRef.current.push(setTimeout(() => {
          pendingTimersRef.current = [];
          setIsGameReady(true);
          setLocalGameReady(true);
          (window as any).phaserGameReady = true;
          console.info('🎮 usePhaserGame: Phaser game ready');
        }, 500));
      }, 100));

    } catch (error) {
      console.error('🎮 usePhaserGame: Failed to create game:', error);
    } finally {
      creatingRef.current = false;
    }
  }, [userId, gameInstance, setGameInstance, setIsGameReady]);

  // =====================================================================================
  // GAME DESTRUCTION
  // =====================================================================================

  const destroyGame = useCallback(() => {
    clearPendingTimers();
    if (gameInstance) {
      console.info('🎮 usePhaserGame: Destroying Phaser game');
      
      try {
        gameInstance.destroy(true);
      } catch (error) {
        console.error('🎮 usePhaserGame: Error destroying game:', error);
        // Continue with cleanup even if destroy fails
      }
      
      // Always clean up state, even if destroy failed
      setGameInstance(null);
      setIsGameReady(false);
      setLocalGameReady(false);
      
      // Clean up window reference
      delete (window as any).phaserGame;
      delete (window as any).phaserGameReady;
      
      // Remove canvas elements
      const canvases = document.querySelectorAll('.phaser-game-canvas');
      canvases.forEach(canvas => canvas.remove());
    }
  }, [gameInstance, setGameInstance, setIsGameReady, clearPendingTimers]);

  // =====================================================================================
  // UTILITY METHODS
  // =====================================================================================

  const getGameScene = useCallback(() => {
    if (!gameInstance || !localGameReady) {
      return null;
    }
    
    try {
      return gameInstance.scene.getScene('scene-game');
    } catch (error) {
      console.warn('🎮 usePhaserGame: Error accessing game scene:', error);
      return null;
    }
  }, [gameInstance, localGameReady]);

  const focusCanvas = useCallback(() => {
    const canvas = document.querySelector('.phaser-game-canvas') as HTMLCanvasElement;
    if (canvas) {
      canvas.focus();
      
      // Re-enable keyboard input in Phaser, unless something (a modal,
      // full-screen video) still holds it suspended
      restorePhaserKeyboard(gameInstance);
    }
  }, [gameInstance]);

  const setCanvasVisible = useCallback((visible: boolean) => {
    const canvas = document.querySelector('.phaser-game-canvas') as HTMLCanvasElement;
    if (canvas) {
      canvas.style.display = visible ? 'block' : 'none';
    }
  }, []);

  // =====================================================================================
  // CANVAS SETUP
  // =====================================================================================

  function setupCanvas(game: Game) {
    const canvases = document.querySelectorAll('canvas');
    canvases.forEach(canvas => {
      // Target the game canvas (large size or direct child of body)
      if ((canvas.parentElement === document.body || 
          (canvas.width > 200 && canvas.height > 200)) && 
          !canvas.classList.contains('phaser-game-canvas')) {
        
        canvas.classList.add('phaser-game-canvas');
        canvas.tabIndex = 0;
        
        // Focus handler for keyboard input
        canvas.addEventListener('click', () => {
          canvas.focus();
          restorePhaserKeyboard(game as unknown as KeyboardGame);
        });
      }
    });
  }

  // =====================================================================================
  // CLEANUP
  // =====================================================================================

  useEffect(() => {
    return () => {
      clearPendingTimers();
      // Only destroy on unmount if this hook created the game
      if (gameInstance && localGameReady) {
        console.info('🎮 usePhaserGame: Cleaning up on unmount');
        destroyGame();
      }
    };
  }, []); // Empty deps - only cleanup on unmount

  // =====================================================================================
  // RETURN VALUE
  // =====================================================================================

  return {
    isGameReady: localGameReady,
    createGame,
    destroyGame,
    getGameScene,
    focusCanvas,
    setCanvasVisible
  };
}